<div class="map-area">
  <!-- The renderer draws into the element of this id -->
  <div id="divFlowsSvg" class="map-canvas"></div>
  <netscope-common-flowview-legend #legend>
    <!-- The apps group or the box of an included apps group clicked on the map, above the legend -->
    @if (selectedAppsGroup) {
      @let levels = selectionNestingLevels;
      @let level = selectionNestingLevel;
      <div class="selection-bar">
        <span class="selection-name" [title]="selectionPath.join(' › ')">
          @for (part of selectionPath; track $index; let last = $last) {
            <span [class.selection-ancestor]="!last">{{ part }}</span>
            @if (!last) {
              <span class="selection-separator">›</span>
            }
          }
        </span>
        <span class="selection-summary">{{ selectionSummary }}</span>

        <span class="selection-nesting" title="How many levels of this group are unfolded, whatever the nesting of the map (+ and − on the keyboard)">
          <label for="apps-groups-map-selection-nesting">Nesting:</label>
          <button type="button" class="btn btn-sm btn-link btn-icon" (click)="stepSelectionNestingLevel(-1)"
                  [disabled]="level == levels[0]" aria-label="Fold one level">−</button>
          <select id="apps-groups-map-selection-nesting" name="apps-groups-map-selection-nesting"
                  [ngModel]="level" (ngModelChange)="setSelectionNestingLevel($event)">
            @if (level == "custom") {
              <option value="custom" disabled>custom</option>
            }
            @for (candidate of levels; track candidate) {
              <option [ngValue]="candidate">{{ nestingLevelLabel(candidate) }}</option>
            }
          </select>
          <button type="button" class="btn btn-sm btn-link btn-icon" (click)="stepSelectionNestingLevel(1)"
                  [disabled]="level == levels[levels.length - 1]" aria-label="Unfold one more level">+</button>
        </span>

        <label class="selection-internal"
               [title]="'Draw the flows between the members and the boxes shown inside ' + selectedAppsGroupName
                        + ': a flow both ends of which are folded into the same box stays hidden'">
          <input type="checkbox" [checked]="selectionShowsInternalFlows" (change)="toggleSelectionInternalFlows()">
          Internal flows{{ selectedIncludedAppsGroupKey != undefined ? ' of ' + selectedAppsGroupName : '' }}
        </label>

        <span class="selection-actions">
          <button type="button" class="btn btn-sm btn-link" (click)="showSelectionFlows()"
                  title="Flows of the members of this group">
            <cds-icon shape="link"></cds-icon> Flows
          </button>
          <button type="button" class="btn btn-sm btn-link" (click)="editSelection()"
                  title="Edit this group in the panel, in the file holding it">
            <cds-icon shape="pencil"></cds-icon> Edit
          </button>
          <button type="button" class="btn btn-sm btn-link btn-icon" (click)="selectAppsGroup(undefined)"
                  title="Clear the selection (Esc)" aria-label="Clear the selection">
            <cds-icon shape="times"></cds-icon>
          </button>
        </span>
      </div>
    }
  </netscope-common-flowview-legend>

  <!-- Floating over the map, which takes the whole box, with the same look as the tools of the flows view -->
  <div class="map-toolbar map-toolbar-start">
    <clr-signpost>
      <cds-icon shape="cog" solid clrSignpostTrigger></cds-icon>
      <clr-signpost-content [clrPosition]="'bottom-right'" *clrIfOpen>
        <label class="clr-control-label">Export:</label>
        <div class="btn-group btn-primary btn-icon">
          <button class="btn btn-primary btn-sm" data-testid="export-svg" (click)="exportAs('svg')" title="Picture that scales without losing quality">SVG</button>
          <button class="btn btn-primary btn-sm" data-testid="export-png" (click)="exportAs('png')" title="Picture for documents and slides">PNG</button>
          <button class="btn btn-primary btn-sm" data-testid="export-drawio" (click)="exportAs('drawio')" title="Diagram to edit in draw.io (diagrams.net)">draw.io</button>
        </div>
      </clr-signpost-content>
    </clr-signpost>

    <span class="camera-buttons">
      <span class="badge" (click)="zoomIn()" title="Zoom in">
        <cds-icon shape="zoom-in"></cds-icon>
        <span class="clr-sr-only">zoom in</span>
      </span>
      <span class="badge" (click)="zoomOut()" title="Zoom out">
        <cds-icon shape="zoom-out"></cds-icon>
        <span class="clr-sr-only">zoom out</span>
      </span>
      <span class="badge" (click)="recenter()" title="Re-center">
        <cds-icon shape="crosshairs"></cds-icon>
        <span class="clr-sr-only">re-center</span>
      </span>

      <span class="badge" title="Split the links by the flows descriptions matching their flows">
        <label for="apps-groups-map-links-mode">Links:</label>
        <select id="apps-groups-map-links-mode" name="apps-groups-map-links-mode"
                [(ngModel)]="appsGroupsLinksMode" (change)="reloadUi()">
          <option value="ignore" title="A single link between two nodes, whatever the descriptions">Ignore descriptions</option>
          <option value="status" title="One link per status of the flows: authorized (green), unauthorized (red), undescribed (grey, dashed)">By status</option>
          <option value="descriptions" title="One link per description matching some of the flows, named after it, and one (grey, dashed) for the undescribed flows">By description</option>
        </select>
      </span>

      <span class="badge" title="How many levels of the apps groups are unfolded when they are drawn: 0 shows each group as a single box, 1 its members and the groups it includes as closed boxes, 2 opens these boxes as well, and so on">
        <label for="apps-groups-map-nesting">Nesting:</label>
        <select id="apps-groups-map-nesting" name="apps-groups-map-nesting"
                [ngModel]="nestingLevel" (ngModelChange)="onNestingLevelChange($event)">
          @for (level of nestingLevels; track level) {
            <option [ngValue]="level">{{ nestingLevelLabel(level) }}</option>
          }
        </select>
      </span>

      @if (groupsWithoutFlowsCount > 0) {
        <span class="badge" title="Draw the groups no flow reaches as well, dimmed and set apart below the others">
          <label class="without-flows-toggle">
            <input type="checkbox" [checked]="showAppsGroupsWithoutFlows" (change)="toggleAppsGroupsWithoutFlows()">
            Groups without flows ({{ groupsWithoutFlowsCount }})
          </label>
        </span>
      }
    </span>

    <select class="label" name="apps-groups-map-routing" aria-label="Links routing" (change)="reloadUi()"
            [(ngModel)]="routingAlgorithm">
      <option value="automatic">Automatic</option>
      <option value="hybrid">Hybrid</option>
      <option value="optimized">Optimized</option>
      <option value="libavoid">Precise</option>
    </select>
    @if (currentRoutingAlgorithm) {
      <span class="label">using {{ currentRoutingAlgorithm }}</span>
    }
    @if (linksRouting) {
      <span class="label"><span class="spinner spinner-inline"></span>routing links…</span>
    }
  </div>

  @if (noGroupHasFlows) {
    <div class="map-notice" role="status">
      <cds-icon shape="info-circle"></cds-icon>
      <span>
        None of the groups of this file exchanged flows with another group over the selected period.
        @if (showAppsGroupsWithoutFlows) {
          They are drawn dimmed, so that you can check what they hold. A longer period may show their flows.
        } @else {
          <button type="button" class="btn btn-sm btn-link" (click)="toggleAppsGroupsWithoutFlows()">Show the groups</button>
        }
      </span>
    </div>
  }

  @if (isSlow) {
    <div class="map-toolbar map-toolbar-end">
      <clr-tooltip>
        <span clrTooltipTrigger>
          <cds-icon shape="exclamation-triangle"></cds-icon>
          slow rendering
        </span>
        <clr-tooltip-content [clrPosition]="'bottom-left'" [clrSize]="'sm'">
          Too many links to route them precisely: a simpler routing is used. Hiding groups or members gives a cleaner map.
        </clr-tooltip-content>
      </clr-tooltip>
    </div>
  }
</div>

<clr-side-panel [clrSidePanelSize]="'lg'" [(clrSidePanelOpen)]="sidePanelOpened" [clrSidePanelStaticBackdrop]="false">
  <h3 class="side-panel-title">
    {{ sidePanelTitle }}
  </h3>
  <div class="side-panel-body">
    @if (sidePanelOpened) {
      <app-netscope-flows-datagrid
        [thirdPartyRefresh]="sidePanelRepository?.refresh"
        [flowsRepository]="sidePanelRepository"
        [totalItemsCount]="sidePanelRepository?.totalItemsCount"
        [flowDetails]="sidePanelRepository?.dashboardFlows"
        [mergeSourceAndTarget]="true"
        [temporalParameter]="temporalParameter"
        [ignoreFilters]="true"
        [showHosts]="false" [hidePacketsExchanged]="true"
        [perPortRow]="true"
        [showPortInline]="true"
        [switchFocus]="switchFocus">
      </app-netscope-flows-datagrid>
    }
  </div>
  <div class="side-panel-footer">
    <button type="button" class="btn btn-primary" (click)="sidePanelOpened = false">Close</button>
  </div>
</clr-side-panel>
